import React, { useState, useEffect, useRef, useMemo } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import DataTable from "react-data-table-component"; // Import DataTable
import Select from "react-select";

function ExcelImport() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [uniqueItemNames, setUniqueItemNames] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);
  const [excelRecords, setExcelRecords] = useState([]); // State for DataTable records
  const [loadingRecords, setLoadingRecords] = useState(true); // Loader for table
  const [notice, setNotice] = useState(null);
  const busy = uploading || deleting || loadingItems || loadingRecords;
  const busyMessage = uploading ? "Uploading Excel file..." : deleting ? "Deleting records..." : "Fetching records...";
  const [filters, setFilters] = useState({ lotNumber: "", itemName: "", grnNo: "" });
  const [resetPagination, setResetPagination] = useState(false);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
    setResetPagination((current) => !current);
  };

  const filteredRecords = useMemo(() => {
    const matches = (value, filter) => String(value ?? "").toLowerCase().includes(filter.trim().toLowerCase());
    return excelRecords.filter((row) =>
      matches(row["LOT NUMBER"], filters.lotNumber) &&
      matches(row["ITEM NAME"], filters.itemName) &&
      matches(row["GRN NO"], filters.grnNo)
    );
  }, [excelRecords, filters]);

  const fileInputRef = useRef(null); // Reference to file input

  const user = JSON.parse(localStorage.getItem("user")) || {}; // Ensure we always get an object
  const loggedInUser = user.Name || "N/A"; // Extract Name, default to "N/A" if not found

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  // ** Fetch Unique Item Names **
  const fetchUniqueItemNames = async () => {
    setLoadingItems(true);
    try {
      const response = await axiosInstance.get("/api/get-unique-item-names");
      setUniqueItemNames(response.data.itemNames);
    } catch (error) {
      console.error("Error fetching item names:", error);
      if (error.response?.status === 404) setUniqueItemNames([]);
      else setNotice({ type: "error", message: "Could not load item names. Please refresh and try again." });
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    fetchUniqueItemNames();
    fetchExcelRecords();
  }, []);

 // ** Fetch Excel Records for DataTable **
 const fetchExcelRecords = async () => {
  setLoadingRecords(true); // Show loader while fetching
  try {
    const response = await axiosInstance.get("/api/get-excel-records");
    setExcelRecords(response.data.records);
  } catch (error) {
    console.error("Error fetching Excel records:", error);
    setNotice({ type: "error", message: "Could not load records. Please refresh and try again." });
  } finally {
    setLoadingRecords(false); // Hide loader after fetching
  }
};


  // ** Handle File Selection **
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0] || null);
  };

  // ** Upload Excel File **
  const handleUpload = async () => {
    if (busy) return;
    if (!selectedFile) {
      showAlert("Please select a file before uploading.", "error");
      return;
    }

    setUploading(true);
    setNotice(null);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("Uploaded_By", loggedInUser);

    try {
    const response  =  await axiosInstance.post("/api/import-excel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    //   showAlert("File uploaded successfully!", "success");
    if (response.status === 200) {
        const rowCount = response.data.count || 0; // Get count from API response
        showAlert(`${rowCount} record(s) inserted successfully!`, "success");
  
        // Reset File Input
        fileInputRef.current.value = "";
        setSelectedFile(null);
      }

      // ** Clear the file input **
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await Promise.all([fetchUniqueItemNames(), fetchExcelRecords()]);
    } catch (error) {
      console.error("Error uploading file:", error);
      showAlert(error.response?.data?.error || "Failed to upload file.", "error");
    } finally {
      setUploading(false);
    }
  };

  // ** Handle Item Selection for Deletion **
  // ** Delete Selected Records **
  const handleDelete = async () => {
    if (busy) return;
    if (!selectedItem) {
      showAlert("Please select an item to delete.", "error");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete all records for "${selectedItem}"?`)) return;

    setDeleting(true);
    try {
      const response = await axiosInstance.delete("/api/delete-stock", {
        data: { ITEM_NAME: selectedItem },
      });

      showAlert(response.data.message, "success");
      await Promise.all([fetchUniqueItemNames(), fetchExcelRecords()]);
      setSelectedItem(""); // Reset selection
    } catch (error) {
      console.error("Error deleting records:", error);
      showAlert(error.response?.data?.error || "Failed to delete records.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const showAlert = (message, type) => {
    setNotice({ message, type });
  };
  // ** Define DataTable Columns **
  const columns = [
    { name: "Lot Number", selector: (row) => row["LOT NUMBER"], sortable: true },
    { name: "Item Name", selector: (row) => row["ITEM NAME"], sortable: true },
    { name: "GRN No", selector: (row) => row["GRN NO"], sortable: true },
    { name: "GRN Date", selector: (row) => row["GRN DATE"], sortable: true },
    { name: "Uploaded By", selector: (row) => row["Uploaded_By"], sortable: true },
  ];
  return (
    <div className="d-flex dashboard excel-import-page" aria-busy={busy}>
      {/* Internal CSS for Loader & Overlay */}
      <style>
        {`
          /* Overlay Effect */
          .excel-import-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(15, 23, 42, 0.55);
            backdrop-filter: blur(3px);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2147483647;
            cursor: wait;
          }

          /* Loader Image */
          .excel-import-loader { background: white; border-radius: 20px; padding: 32px 44px; text-align: center; box-shadow: 0 20px 60px #0003; max-width: 90vw; }
          .excel-import-page .main-container { background: #f5f7fb; min-height: 100vh; }
          .excel-import-card { background: white; border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px; box-shadow: 0 4px 16px #0f172a05; }
          .excel-import-actions { display: grid; grid-template-columns: 1.2fr 1fr; gap: 20px; margin: 24px 0; }
          .excel-import-page .form-control, .excel-import-page .form-select { border-radius: 9px; min-height: 44px; }
          .excel-import-page .btn { border-radius: 9px; min-height: 42px; }
          .excel-import-page .form-label { font-weight: 600; color: #475569; }
          .excel-import-page fieldset { min-width: 0; }
          @media (max-width: 768px) { .excel-import-actions { grid-template-columns: 1fr; } .excel-import-card { padding: 16px; } }
        `}
      </style>

      {/* Loader Overlay (Only show if uploading & file is selected) */}
      {busy && (
        <div className="excel-import-overlay" role="status" aria-live="polite">
          <div className="excel-import-loader">
            <span className="spinner-border text-primary mb-3" aria-hidden="true" />
            <h5>{busyMessage}</h5>
            <div className="text-muted">Please wait until the operation finishes.</div>
          </div>
        </div>
      )}
      <div className="d-flex w-100" inert={busy ? "" : undefined}>
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />
        <main className="main-container p-4"  style={{ height: "fit-content" }}>
          <h3 className="mb-1 fw-bold">Purchase Store – Excel Import</h3>
          <p className="text-muted mb-0">Upload stock records and find imported lots quickly.</p>
          {notice && <div className={`alert alert-${notice.type === "success" ? "success" : "danger"} mt-3`} role="alert">{notice.message}</div>}
          <fieldset disabled={busy} className="border-0 p-0 m-0">

      <div className="excel-import-actions">
          {/* Upload Section */}
          <div className="excel-import-card">
            <h5 className="mb-1">Upload stock file</h5>
            <p className="text-muted small">Select an Excel file to import stock records.</p>
            <label className="form-label" htmlFor="excel-upload">Upload Excel File</label>
            <input
              id="excel-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="form-control "
              ref={fileInputRef} // Reference to clear input after upload
            />
            <button className="btn btn-primary mt-2" onClick={handleUpload} disabled={uploading || !selectedFile}>
              {uploading ? "Uploading..." : "Upload Excel"}
            </button>
          </div>

          {/* Select & Delete Section */}
          <div className="excel-import-card">
            <h5 className="mb-1">Manage records</h5>
            <p className="text-muted small">Delete all imported records for the selected item.</p>
            <label className="form-label" htmlFor="excel-delete-item">Select Item to Delete</label>
            <Select
              inputId="excel-delete-item"
              options={uniqueItemNames.map((name) => ({ value: name, label: name }))}
              value={selectedItem ? { value: selectedItem, label: selectedItem } : null}
              onChange={(option) => setSelectedItem(option?.value || "")}
              isSearchable
              isClearable
              isDisabled={busy}
              isLoading={loadingItems}
              placeholder="Search and select an item..."
              noOptionsMessage={() => "No matching items found"}
              styles={{
                control: (base) => ({ ...base, minHeight: 44, borderRadius: 9 }),
                menu: (base) => ({ ...base, zIndex: 20 }),
              }}
            />
            <button className="btn btn-danger mt-2" onClick={handleDelete} disabled={deleting || !selectedItem}>
              {deleting ? "Deleting..." : "Delete Records"}
            </button>
          </div>
          </div>
          <div className="excel-import-card">
          <h5 className="mb-0">Imported records</h5>
          <div className="row g-3 mt-3 mb-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label" htmlFor="excel-lot-filter">Lot Number</label>
              <input id="excel-lot-filter" type="search" className="form-control" placeholder="Filter lot number" value={filters.lotNumber} onChange={(e) => updateFilter("lotNumber", e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="excel-item-filter">Item Name</label>
              <input id="excel-item-filter" type="search" className="form-control" placeholder="Filter item name" value={filters.itemName} onChange={(e) => updateFilter("itemName", e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="excel-grn-filter">GRN No</label>
              <input id="excel-grn-filter" type="search" className="form-control" placeholder="Filter GRN number" value={filters.grnNo} onChange={(e) => updateFilter("grnNo", e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary" onClick={() => {
                setFilters({ lotNumber: "", itemName: "", grnNo: "" });
                setResetPagination((current) => !current);
              }}>Clear Filters</button>
            </div>
          </div>
          <div className="text-muted mb-2">Showing {filteredRecords.length} of {excelRecords.length} records</div>
         {/* Show loader while fetching records */}
          {loadingRecords ? (
            <div className="text-center my-3">
              <span className="spinner-border text-primary"></span> Loading data...
            </div>
          ) : (
            <DataTable columns={columns} data={filteredRecords} keyField="ID" pagination paginationResetDefaultPage={resetPagination} highlightOnHover noDataComponent="No records match the selected filters." />
          )}
          </div>
          </fieldset>
        </main>
      </div>
      </div>
    </div>
  );
}

export default ExcelImport;
